import type { TFunction } from 'i18next';
import type { FC } from 'react';
import type { TranslatedProperty } from '@/types/domain/core';
import { Button, Flex, Icon, Section, Skeleton, Typography } from '@ui';
import dayjs from 'dayjs';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { AirportRunways } from '@/components/AirportDetailed/components/AirportRunways/AirportRunways';
import { CodesPositions } from '@/components/AirportDetailed/components/CodesPositions/CodesPositions';
import { Weather } from '@/components/Weather/Weather';
import { useAirport } from '@/queries/useAirport';
import styles from './AirportDetailed.module.css';

const SHOW_MORE = false;

type Props = {
    airportId: number;
};

const description = ['90%', '30%'];

const fallback = (t: TFunction) => (
    <>
        <CodesPositions.Skeleton />
        <Flex vertical gap={4}>
            {description.map(width => <Skeleton key={width} width={width} height={18} />)}
        </Flex>
        <Flex gap={32}>
            <AirportRunways.Skeleton />
            <Flex vertical gap={24} flex="0 0 calc(70% - 16px)">
                <Typography.Heading level={4} color="textSecondary">{t('Airports_Card_Weather')}</Typography.Heading>
                <Weather.Skeleton />
            </Flex>
        </Flex>
    </>
);

export const AirportDetailed: FC<Props> = ({ airportId }) => {
    const { data: airport, dataUpdatedAt } = useAirport(airportId);
    const { t, i18n } = useTranslation();

    const lang = i18n.language as keyof TranslatedProperty;

    return (
        <Flex className={styles.container}>
            <Section size="m" noPadding block>
                <Flex vertical className={styles.innerContainer}>
                    <Flex vertical gap={52} flex="1 0">
                        <Suspense fallback={fallback(t)}>
                            <CodesPositions airport={airport} />
                            <div className={styles.description}>
                                <Markdown>{airport.description[lang]}</Markdown>
                            </div>
                            <Flex gap={32}>
                                <AirportRunways airport={airport} />
                                <Flex vertical gap={24} flex="0 0 calc(70% - 16px)">
                                    <Typography.Heading level={4} color="textSecondary">{t('Airports_Card_Weather')}</Typography.Heading>
                                    <Weather airportId={airport.id} />
                                </Flex>
                            </Flex>
                        </Suspense>
                    </Flex>
                    <Flex align="flex-end" justify="space-between" flex="0 0">
                        {SHOW_MORE ? <Button variant="secondary" icon={<Icon.Arrow />}>{t('Airports_Card_More')}</Button> : <div />}
                        <Typography.Description color="textTertiary">
                            {`${t('Airports_Card_Disclaimer_Short')}. ${t('Airports_Card_LastUpdated')}: ${dayjs.utc(dataUpdatedAt).format('HH:mm')} UTC`}
                        </Typography.Description>
                    </Flex>
                </Flex>
            </Section>
        </Flex>
    );
};
